// Shared pure helpers for the six action button components. These were
// previously copy-pasted into every component body — the null-guard fix
// for unbound commands had to be applied six times, and the copies had
// already drifted. One definition, six importers.
import { ELEVATOR_CONTROLS } from "../../../constants/action";
import { MessageInterface } from "../../../interfaces/Message.interface";
import { ActionCommandOption } from "../../../interfaces/Bank.interface";
import { UuidT } from "../../../flatbuffers/generated/front-end/uuid";
import { uuidToString } from "../../../utils/uuid";
import { devWarn } from "../../../utils/devLog";
import notifyCommandBlocked from "../../../utils/commandFeedback";

/** Split PascalCase into words: "CloseDoor" → "Close Door". */
export const formatCamelCaseLabel = (value: string): string =>
  value.replace(/([a-z])([A-Z])/g, "$1 $2");

/**
 * Human-readable name for a Control enum value. Returns "" for null
 * (unbound command — enum 0 is a real Control, CarCallLock, so callers
 * must pass null rather than a 0 sentinel) and "Unknown" for values the
 * ELEVATOR_CONTROLS catalog doesn't list.
 */
export const getElevatorControlNameById = (id: number | null): string => {
  if (id == null) return "";
  const control = ELEVATOR_CONTROLS.find((item) => item.id === id);
  if (!control?.name) return "Unknown";

  return formatCamelCaseLabel(control.name);
};

/**
 * Message ids reach these components as plain catalog strings ("CONTROL:21")
 * on the monitoring path and as FlatBuffer UuidT values on the typed paths.
 * Strings pass through untouched so an unbound "" stays "" rather than being
 * widened to the zero UUID.
 */
const normalizeMessageId = (id: string | UuidT): string =>
  typeof id === "string" ? id : uuidToString(id);

/**
 * The enum value a Message record carries, under either spelling.
 *
 * useSchemaMessages builds its catalog with `enumId`; records that predate it
 * carry `enum_id`. MessageInterface's index signature means the snake_case
 * probe type-checks either way, so a wrong guess here is a silent runtime
 * undefined, not a compile error — probe both.
 */
type EnumBearingMessage = { enumId?: number | null; enum_id?: number | null };

/**
 * Resolve an action's bound command to its enum value, or null when the id
 * matches no message in the catalog.
 *
 * "Unresolved" MUST be null, never 0. Of the nine message categories only
 * CONTROLLER_INFO declares a NONE member; the other eight are unnumbered and
 * append-only, so declaration order is the wire value and each one's first
 * variant is a legitimate 0 — Control.CarCallLock, Service.Auto,
 * Status.HighSpeedStartSequence. A 0 sentinel would make an unbound button
 * fire CarCallLock.
 */
export const resolveMessageEnum = (
  messages: MessageInterface[],
  id: string | UuidT,
): number | null => {
  const key = normalizeMessageId(id);
  const msg = messages.find((m) => String(m.id) === key) as EnumBearingMessage | undefined;
  const enumValue = msg?.enumId ?? msg?.enum_id;
  return typeof enumValue === "number" ? enumValue : null;
};

/**
 * A catalog command option, or undefined when the value is not one.
 *
 * All three fields must be present with the right types: a half-populated
 * object counts as absent, not as partial, so a malformed one can never put a
 * command on the wire. The numeric check is `typeof === "number"` and never
 * truthiness — `Control.CarCallLock` is a legitimate 0, and a `!opt.numeric`
 * guard would drop exactly that command.
 */
export const asCommandOption = (value: unknown): ActionCommandOption | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const opt = value as Partial<ActionCommandOption>;
  if (typeof opt.label !== "string" || typeof opt.value !== "string") return undefined;
  if (typeof opt.numeric !== "number") return undefined;
  return opt as ActionCommandOption;
};

/** @see asCommandOption — the same validation, narrowed to the enum. */
export const commandOptionEnum = (option: ActionCommandOption | null | undefined): number | null =>
  asCommandOption(option)?.numeric ?? null;

/**
 * The enum a button must actually send.
 *
 * Order matters and is deliberately conservative: the message catalog is tried
 * FIRST, because `normalizeActionMessageIds` has already folded the row's `cmd`
 * — the canonical runtime field — into `messageId`. The row's own command
 * option is the fallback, so this can only ever rescue a button that resolves
 * to nothing today; it never changes what an already-working button sends, and
 * a `command` object that disagrees with `cmd` cannot override it.
 *
 * The fallback matters for commands the generated schema catalog does not
 * declare (adapter-only control writes), where `catalogIdForEnum` finds no
 * message to map the enum through and `messageId` stays an unresolvable id.
 */
export const resolveCommandEnum = (
  messages: MessageInterface[],
  id: string | UuidT,
  command?: ActionCommandOption | null,
): number | null => resolveMessageEnum(messages, id) ?? commandOptionEnum(command);

/**
 * Display name for a bound command: the humanised `Control` name, falling back
 * to the catalog option's own member name.
 *
 * The fallback is what names an adapter-only command. Those resolve to an enum
 * the ELEVATOR_CONTROLS catalog does not list, so `getElevatorControlNameById`
 * answers "Unknown" — the option carries the real member name, and humanising
 * it reads the same way as every other row ("SecureCarCall" → "Secure Car
 * Call") instead of showing the operator a dead end.
 */
export const resolveCommandDisplayName = (
  messages: MessageInterface[],
  id: string | UuidT,
  command?: ActionCommandOption | null,
): string => {
  const name = getElevatorControlNameById(resolveCommandEnum(messages, id, command));
  if (name !== "" && name !== "Unknown") return name;
  const value = command?.value;
  return typeof value === "string" && value !== "" ? formatCamelCaseLabel(value) : name;
};

// ---------------------------------------------------------------------------
// DIAGNOSTICS
// "The button does nothing" has several distinct causes that all end in
// `resolveMessageEnum` returning null, and the null tells you nothing about
// which one you hit. These name it.
// ---------------------------------------------------------------------------

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ZERO_UUID = "00000000-0000-0000-0000-000000000000";

/** How the catalog's own ids are keyed, which decides what CAN ever match. */
type CatalogKeyStyle = "empty" | "enum" | "uuid" | "mixed";

export interface MessageIdDiagnosis {
  /** What the caller was handed. */
  shape: "absent" | "string" | "UuidT";
  /** The key actually used for the lookup. */
  normalized: string;
  looksLikeUuid: boolean;
  isZeroUuid: boolean;
  catalogSize: number;
  catalogKeyStyle: CatalogKeyStyle;
  matched: boolean;
  /** One sentence naming the cause, for a console or a bug report. */
  verdict: string;
}

const classifyCatalog = (messages: MessageInterface[]): CatalogKeyStyle => {
  if (messages.length === 0) return "empty";
  let uuids = 0;
  messages.forEach((m) => {
    if (UUID_PATTERN.test(String(m.id))) uuids += 1;
  });
  if (uuids === 0) return "enum";
  if (uuids === messages.length) return "uuid";
  return "mixed";
};

/**
 * Explain why a message id does or does not resolve to a command enum.
 *
 * The interesting case is the fourth: `useGetAllMessages` is schema-driven
 * (useSchemaMessages) and builds its ids as `"<CATEGORY>:<enumValue>"`, e.g.
 * "CONTROL:21", straight off the generated FlatBuffer enums. An action row
 * whose `messageId` is a Message-row UUID — which is what the typed/binary API
 * path carries — therefore cannot match ANY entry, no matter how correct the
 * UUID is. The UuidT is converted fine; there is simply nothing to convert it
 * into. That needs a UUID → enum mapping upstream, not a fix in this lookup.
 */
export const diagnoseMessageId = (
  messages: MessageInterface[],
  id: string | UuidT | null | undefined,
): MessageIdDiagnosis => {
  const shapeOf = (): MessageIdDiagnosis["shape"] => {
    if (id == null) return "absent";
    return typeof id === "string" ? "string" : "UuidT";
  };
  const shape = shapeOf();
  const normalized = id == null ? "" : normalizeMessageId(id);
  const looksLikeUuid = UUID_PATTERN.test(normalized);
  const isZeroUuid = normalized === ZERO_UUID;
  const catalogSize = messages.length;
  const catalogKeyStyle = classifyCatalog(messages);
  const matched = id != null && resolveMessageEnum(messages, id) != null;

  const verdict = (() => {
    if (matched) return "Resolves to a command enum — this id is fine.";
    if (shape === "absent" || normalized === "")
      return "No command is bound: the id is empty. The action was saved without one.";
    if (isZeroUuid)
      return "The id is the nil UUID (00000000-...), which is what an unset UuidT converts to. The action was saved without a command.";
    if (looksLikeUuid && catalogKeyStyle === "enum")
      return `The id is a Message-row UUID, but the message catalog is keyed by enum ("CONTROL:21" style) — useGetAllMessages is schema-driven and holds no UUIDs, so no UUID can ever match. The UuidT converted correctly; there is nothing in the catalog to convert it into.`;
    if (catalogKeyStyle === "empty")
      return "The message catalog is empty, so nothing can resolve yet.";
    return `No catalog entry has this id (${catalogSize} entries, ${catalogKeyStyle}-style keys).`;
  })();

  return {
    shape,
    normalized,
    looksLikeUuid,
    isZeroUuid,
    catalogSize,
    catalogKeyStyle,
    matched,
    verdict,
  };
};

/** Ids already reported, so a re-render does not repeat the same warning. */
const reportedMessageIds = new Set<string>();

/**
 * Warn once per unresolved id, through devWarn (development builds only).
 *
 * Deliberately silent when the id resolves: this is for the case where a button
 * is on screen, looks enabled, and sends nothing.
 */
export const logUnresolvedMessageId = (
  scope: string,
  label: string,
  messages: MessageInterface[],
  id: string | UuidT | null | undefined,
): MessageIdDiagnosis | null => {
  const diagnosis = diagnoseMessageId(messages, id);
  if (diagnosis.matched) return null;
  if (process.env.NODE_ENV === "production") return diagnosis;

  const key = `${scope}|${label}|${diagnosis.normalized}`;
  if (reportedMessageIds.has(key)) return diagnosis;
  reportedMessageIds.add(key);

  devWarn(
    `[actionButtons] ${scope} command of "${label}" resolves to no enum. ${diagnosis.verdict}`,
    { ...diagnosis, rawId: id, catalogIdSample: messages.slice(0, 3).map((m) => m.id) },
  );
  return diagnosis;
};

/**
 * Report an action whose configured command resolves to nothing, and return
 * false so the caller can bail with `if (!assertBoundCommand(...)) return;`.
 *
 * Every one of the six buttons previously wrote `if (messageEnum == null)
 * return;` — correct in that it refuses to guess a command (enum 0 is the real
 * Control.CarCallLock), but invisible: the operator clicks a button that looks
 * enabled and absolutely nothing happens, on-screen or on the wire. The button
 * cannot know WHY the binding is missing, so the message points at the setup
 * wizard rather than inventing a cause.
 *
 * Returns a boolean instead of throwing because these are click handlers; an
 * exception here would surface as an unhandled rejection, not as feedback.
 */
/**
 * Why a row is shown but cannot be pressed.
 *
 * Deliberately names the remedy and the place to apply it. The most common
 * cause is a row bound to a message that is not a Control command at all: the
 * LN2 categories are separate enums whose values collide, so such a row used to
 * resolve to whichever Control member shared its number and offer to send that
 * instead.
 */
export const UNBOUND_ACTION_REASON =
  "This action is not linked to a control command the adapter accepts, so it cannot be sent. " +
  "Bind a command for it in the setup wizard's Actions step, or delete it there.";

/**
 * Tooltip for a button whose command resolves to nothing, shown BEFORE the
 * press rather than after it.
 *
 * assertBoundCommand below is the dispatch-time backstop and stays, but it
 * cannot be the only disclosure. A row's secondary command is not covered by
 * ActionCard's `unbound` check — that deliberately tests only the primary, so
 * that a row whose pair is missing can still send the half that works — which
 * left the second button looking live and reporting "not linked to a control
 * message" only after it had been pressed. The operator reads that as a
 * contradiction, because the row's OTHER button did reach the controller.
 *
 * Disabling instead of hiding: the same reason the button is rendered at all.
 * A hidden secondary makes a misconfigured two-command row look like a
 * correctly configured single-command row, which is the one failure an
 * operator cannot report. Visible, disabled and explained is the state that
 * says "this is broken, and here is where to fix it".
 */
export const UNBOUND_COMMAND_REASON =
  "No control command is bound to this button, so it cannot send anything. " +
  "Bind one for this action in the setup wizard's Actions step.";

/**
 * Tooltip for the PRIMARY button of a row whose two commands are not both
 * bound.
 *
 * HOLDBACK, TURNBACK and ENERGIZER_TURN_BACK dispatch their pair from the
 * primary's own press — on release, or when the countdown ends — never from a
 * control the operator touches. So an unbound pair on those rows could only
 * ever be reported AFTER the primary had already reached the controller, which
 * is what made the feedback read as self-contradictory: the same press said
 * "Command sent via WebSocket" and "nothing was sent".
 *
 * These rows are refused rather than half-run. The pair is not a nicety on
 * them: it is the leg that clears what the primary asserts, so sending only
 * the primary leaves a bit set with nothing configured to release it.
 */
export const UNBOUND_PAIR_REASON =
  "This action runs two commands and one of them is not bound, so it cannot run. " +
  "Bind both for this action in the setup wizard's Actions step.";

export const assertBoundCommand = (
  enumValue: number | null,
  label: string,
  theme?: string,
): enumValue is number => {
  if (enumValue != null) return true;

  notifyCommandBlocked(
    "Action has no command bound",
    `"${label}" is not linked to a control message, so nothing was sent. Bind a command for this action in the setup wizard's Actions step.`,
    theme,
  );
  return false;
};

/**
 * Resolve an action's valid-state message ids to the enum values compared
 * against the car's live state. Ids that match no message are dropped;
 * a resolved 0 is kept, for the reason above.
 */
export const resolveValidStateEnums = (
  messages: MessageInterface[],
  ids: Array<string | UuidT>,
): number[] =>
  ids
    .map((id) => resolveMessageEnum(messages, id))
    .filter((enumValue): enumValue is number => enumValue !== null);

/**
 * Human-readable names for a list of message ids, for display only.
 *
 * The info popover used to print the raw resolved enum numbers ("3", "17"),
 * which tells an operator nothing. useSchemaMessages already carries a
 * humanised `shortMessage` per catalog entry, so prefer it and fall back to the
 * Control name for ids that resolve to an enum but carry no short message.
 * Ids matching nothing are dropped rather than shown as blanks.
 */
export const resolveMessageDisplayNames = (
  messages: MessageInterface[],
  ids: Array<string | UuidT>,
): string[] =>
  ids
    .map((id) => {
      const key = normalizeMessageId(id);
      const msg = messages.find((m) => String(m.id) === key);
      if (!msg) return "";
      if (typeof msg.shortMessage === "string" && msg.shortMessage !== "") return msg.shortMessage;
      const enumValue = resolveMessageEnum(messages, id);
      return getElevatorControlNameById(enumValue);
    })
    .filter((name) => name !== "");

/**
 * The position a command dispatches to, from the row's selected target floor.
 *
 * Three states travel on one prop, and the distinction is load-bearing:
 *
 *  - `undefined` — this row's command does not take a floor (VIP, CarToLobby,
 *    IndependentService). It dispatches at 0, exactly as it always has.
 *  - a number — the operator picked a floor on the row's strip; dispatch there.
 *  - `null` — the command DOES take a floor and none has been picked yet. The
 *    caller must not dispatch at all; `floorSelectionMissing` reports that so
 *    the buttons can be disabled with a stated reason. The `?? 0` here is only
 *    a backstop for a caller that ignores the gate.
 *
 * Collapsing `undefined` and `null` would either dead-lock every non-positional
 * row or restore the bug this exists to fix — a positional command sent to a
 * position the operator never chose.
 */
export const dispatchFloor = (targetFloor: number | null | undefined): number => targetFloor ?? 0;

/**
 * Whether the row is waiting on a floor choice, and must therefore not dispatch.
 *
 * ONLY an explicit `null` blocks. `undefined` is the default every
 * non-positional row carries, so omitting the prop can never disable a button
 * that works today.
 */
export const floorSelectionMissing = (targetFloor: number | null | undefined): boolean =>
  targetFloor === null;
